// src/index.ts
import { initContract as initContract11 } from "@ts-rest/core";

// src/auth.ts
import { initContract } from "@ts-rest/core";
import { z as z2 } from "zod";
import { ErrorSchema } from "@gb/contracts";

// src/schemas.ts
import { z } from "zod";
import { AdminUserStatusSchema, AuthTokensSchema } from "@gb/contracts";
import { AdminUserStatusSchema as AdminUserStatusSchema2 } from "@gb/contracts";
var AdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  status: AdminUserStatusSchema,
  /** Null until the first successful Google sign-in. */
  lastLoginAt: z.string().nullable(),
  createdAt: z.string()
});
var AdminSessionSchema = z.object({
  admin: AdminUserSchema,
  tokens: AuthTokensSchema
});
var AdminTokensResponseSchema = z.object({ tokens: AuthTokensSchema });
var AdminMeResponseSchema = z.object({ admin: AdminUserSchema });
var GoogleAuthUrlResponseSchema = z.object({
  url: z.string().url(),
  state: z.string()
});
var GoogleCallbackInputSchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1)
});
var AdminRefreshInputSchema = z.object({
  refreshToken: z.string().min(1)
});
var AdminTenantStatusSchema = z.enum(["provisioning", "active", "suspended"]);
var AdminTenantSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  status: AdminTenantStatusSchema,
  /** Login routing: which email domains resolve to this tenant. */
  emailDomains: z.array(z.string()),
  /**
   * The config the tenant runs on: `tenant_registry.config` as
   * `parseTenantConfig` reads it, with the defaults of settings added since
   * the row was written filled in, and unknown keys dropped (2026-10-03,
   * `document-ai/0032`: a tenant stored before the category list existed
   * showed none). Passed through rather than re-declared:
   * `TenantConfigSchema` lives in `@gb/db` (which this package must not depend
   * on), and the backoffice renders config as data, not as a form it could
   * get out of sync with.
   */
  config: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string()
});
var AdminTenantStatsSchema = z.object({
  users: z.number().int().nonnegative(),
  projects: z.number().int().nonnegative(),
  documents: z.number().int().nonnegative(),
  journalEntries: z.number().int().nonnegative(),
  chatMessages: z.number().int().nonnegative(),
  /** Newest chat message, or null for a tenant nobody has asked anything. */
  lastActivityAt: z.string().nullable()
});
var AdminTenantUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string().nullable(),
  /** May this account turn on the dashboard's debug view (`architecture/0029`). */
  canDebug: z.boolean(),
  createdAt: z.string()
});
var AdminJobSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid().nullable(),
  importId: z.string().uuid().nullable(),
  kind: z.string(),
  status: z.string(),
  error: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  /** Which worker holds it, while it runs. The first thing to read on a stuck job. */
  claimedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
var AdminPageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0)
});

// src/auth.ts
var c = initContract();
var adminAuthContract = c.router({
  googleAuthUrl: {
    method: "GET",
    path: "/auth/google/url",
    responses: {
      200: GoogleAuthUrlResponseSchema,
      // The backoffice is an optional integration: unset Google client or
      // admin secret means these routes answer 503, and the tenant API is
      // untouched.
      503: ErrorSchema
    },
    summary: "Mint the Google consent URL and a signed state"
  },
  googleCallback: {
    method: "POST",
    path: "/auth/google/callback",
    body: GoogleCallbackInputSchema,
    responses: {
      200: AdminSessionSchema,
      // Bad state, failed exchange, unverified email, wrong hosted domain,
      // unknown email, disabled admin, and a google_sub mismatch all return
      // the *same* 403 — none of them may be used to probe who is an admin.
      403: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Exchange the Google code for an admin session"
  },
  refresh: {
    method: "POST",
    path: "/auth/refresh",
    body: AdminRefreshInputSchema,
    responses: {
      200: AdminTokensResponseSchema,
      401: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Exchange an admin refresh token for a new pair"
  },
  logout: {
    method: "POST",
    path: "/auth/logout",
    body: AdminRefreshInputSchema,
    responses: {
      200: z2.object({ success: z2.literal(true) }),
      401: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Revoke an admin refresh token"
  },
  me: {
    method: "GET",
    path: "/auth/me",
    headers: z2.object({ authorization: z2.string() }),
    responses: {
      200: AdminMeResponseSchema,
      401: ErrorSchema
    },
    summary: "Return the currently authenticated admin"
  }
});

// src/tenants.ts
import { initContract as initContract2 } from "@ts-rest/core";
import { z as z3 } from "zod";
import { ErrorSchema as ErrorSchema2 } from "@gb/contracts";
var c2 = initContract2();
var TenantSlugSchema = z3.string().regex(/^[a-z][a-z0-9_]{1,48}$/, "lowercase letters, digits and _, starting with a letter");
var AdminTenantCreateSchema = z3.object({
  slug: TenantSlugSchema,
  name: z3.string().trim().min(1).max(200),
  /** Login routing — at least one domain, lowercase, no `@`. */
  emailDomains: z3.array(z3.string().trim().min(1)).min(1).max(20),
  /**
   * Passed through as data, validated server-side by `TenantConfigSchema`
   * (strict on write) — same reasoning as `AdminTenantSchema.config`.
   */
  config: z3.record(z3.unknown()).optional()
});
var AdminTenantUserCreateSchema = z3.object({
  email: z3.string().trim().toLowerCase().email(),
  name: z3.string().trim().min(1).max(200).optional()
});
var AdminSetUserDebugSchema = z3.object({ canDebug: z3.boolean() });
var AdminSetMicrosoftSchema = z3.object({
  tenantId: z3.string().trim().toLowerCase().uuid("That isn\u2019t a Microsoft organisation id. It looks like 3f2b9c1e-6a4d-4b8e-9f10-2c7d5e8a1b40.").nullable()
});
var AdminMicrosoftConsentLinkSchema = z3.object({
  url: z3.string().url()
});
var adminTenantsContract = c2.router({
  list: {
    method: "GET",
    path: "/tenants",
    responses: {
      200: z3.object({ tenants: z3.array(AdminTenantSchema) }),
      401: ErrorSchema2
    },
    summary: "Every tenant in the registry, whatever its status"
  },
  get: {
    method: "GET",
    path: "/tenants/:tenantId",
    responses: {
      200: z3.object({ tenant: AdminTenantSchema }),
      401: ErrorSchema2,
      404: ErrorSchema2
    },
    summary: "One tenant's registry row"
  },
  stats: {
    method: "GET",
    path: "/tenants/:tenantId/stats",
    responses: {
      200: AdminTenantStatsSchema,
      401: ErrorSchema2,
      404: ErrorSchema2
    },
    summary: "Row counts and last activity, computed from the tenant's schema"
  },
  users: {
    method: "GET",
    path: "/tenants/:tenantId/users",
    responses: {
      200: z3.object({ users: z3.array(AdminTenantUserSchema) }),
      401: ErrorSchema2,
      404: ErrorSchema2
    },
    summary: "The tenant's user accounts (no credentials, ever)"
  },
  create: {
    method: "POST",
    path: "/tenants",
    body: AdminTenantCreateSchema,
    responses: {
      201: z3.object({ tenant: AdminTenantSchema }),
      400: ErrorSchema2,
      401: ErrorSchema2,
      // A slug or domain already held. Deliberately not idempotent even though
      // `provisionTenant` is: a double-submit silently reusing a tenant would
      // read as "created", and the second admin would onboard onto the first
      // admin's tenant.
      409: ErrorSchema2
    },
    summary: "Provision a tenant: registry row, domains, schema, reference data"
  },
  createUser: {
    method: "POST",
    path: "/tenants/:tenantId/users",
    body: AdminTenantUserCreateSchema,
    responses: {
      /**
       * The one response in either contract that carries a secret: the
       * generated password, shown exactly once. It is never stored (only its
       * argon2 hash is), never logged, and not retrievable by any later call —
       * the recorded exception to "no endpoint returns a secret", see
       * `architecture/0027`.
       */
      201: z3.object({ user: AdminTenantUserSchema, password: z3.string() }),
      401: ErrorSchema2,
      404: ErrorSchema2,
      409: ErrorSchema2
    },
    summary: "Create a user with a generated one-time password, returned once"
  },
  /**
   * Record the tenant's Microsoft organisation for the SharePoint and Outlook
   * syncs, or clear it. The debug flag's route is `setUserDebug` below
   * (`architecture/0029`): set in production without a database console.
   */
  setMicrosoft: {
    method: "PUT",
    path: "/tenants/:tenantId/microsoft",
    body: AdminSetMicrosoftSchema,
    responses: {
      200: z3.object({ tenant: AdminTenantSchema }),
      400: ErrorSchema2,
      401: ErrorSchema2,
      404: ErrorSchema2,
      // Another tenant already records that organisation.
      409: ErrorSchema2
    },
    summary: "Record the tenant's Microsoft organisation for SharePoint sync, or clear it"
  },
  microsoftConsentLink: {
    method: "GET",
    path: "/tenants/:tenantId/microsoft/consent-link",
    responses: {
      200: AdminMicrosoftConsentLinkSchema,
      401: ErrorSchema2,
      404: ErrorSchema2,
      503: ErrorSchema2
    },
    summary: "The admin-consent address to send the tenant's Microsoft 365 admin"
  },
  setUserDebug: {
    method: "PUT",
    path: "/tenants/:tenantId/users/:userId/debug",
    body: AdminSetUserDebugSchema,
    responses: {
      200: z3.object({ user: AdminTenantUserSchema }),
      400: ErrorSchema2,
      401: ErrorSchema2,
      404: ErrorSchema2
    },
    summary: "Let one account turn on the debug view, or stop it"
  }
});

// src/projects.ts
import { initContract as initContract4 } from "@ts-rest/core";
import { z as z5 } from "zod";
import {
  DocumentLinkSchema,
  ErrorSchema as ErrorSchema4,
  FamilyRoleSchema,
  LinkFoundBySchema,
  LinkKindSchema,
  ImportsResponseSchema,
  MadeBySchema,
  ProjectBriefResponseSchema,
  PhasesInputSchema,
  PhasesResponseSchema,
  ProcessingStepSchema as ProcessingStepSchema2,
  DocumentQuestionSchema,
  ProjectBasicsSchema,
  ProjectChangeKindSchema,
  ProjectSchema,
  UndoResponseSchema
} from "@gb/contracts";

// src/documents.ts
import { initContract as initContract3 } from "@ts-rest/core";
import { z as z4 } from "zod";
import {
  DocumentAssetsResponseSchema,
  DocumentChunkSchema,
  DocumentSegmentsResponseSchema,
  ErrorSchema as ErrorSchema3,
  ProcessingStepOutcomeSchema,
  ProcessingStepSchema,
  ProjectDocumentSchema,
  UploadUrlInputSchema,
  UploadUrlResponseSchema
} from "@gb/contracts";
var c3 = initContract3();
var ProcessingStepRowSchema = z4.object({
  id: z4.string().uuid(),
  /** Groups one run's rows. The parse job's id, reused when the sweeper re-runs it. */
  runId: z4.string().uuid(),
  /** Tells apart the runs that share a `runId`. */
  attempt: z4.number().int().min(1),
  step: ProcessingStepSchema,
  stepVersion: z4.number().int(),
  outcome: ProcessingStepOutcomeSchema,
  /** User-safe, when there is anything to say. */
  reason: z4.string().nullable(),
  /** Internal, backoffice only. Free-form by design: each step says what it needs to. */
  detail: z4.record(z4.unknown()).nullable(),
  /** Null until the fetch step has read the bytes. */
  fileSha256: z4.string().nullable(),
  /** ISO 8601. */
  startedAt: z4.string(),
  /** ISO 8601. */
  finishedAt: z4.string()
});
var adminDocumentsContract = c3.router({
  list: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/documents",
    query: AdminPageQuerySchema,
    responses: {
      200: z4.object({
        documents: z4.array(ProjectDocumentSchema),
        total: z4.number().int().min(0)
      }),
      401: ErrorSchema3,
      404: ErrorSchema3
    },
    summary: "The project's documents, newest first"
  },
  get: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/documents/:docId",
    responses: {
      200: z4.object({ document: ProjectDocumentSchema }),
      401: ErrorSchema3,
      404: ErrorSchema3
    },
    summary: "One document's row"
  },
  segments: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/documents/:docId/segments",
    responses: {
      200: DocumentSegmentsResponseSchema,
      401: ErrorSchema3,
      404: ErrorSchema3
    },
    summary: "The document's citable surfaces (pages/sheets)"
  },
  assets: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/documents/:docId/assets",
    responses: {
      200: DocumentAssetsResponseSchema,
      401: ErrorSchema3,
      404: ErrorSchema3
    },
    summary: "Images lifted out of the document, with their vision output"
  },
  chunks: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/documents/:docId/chunks",
    responses: {
      200: z4.object({ chunks: z4.array(DocumentChunkSchema) }),
      401: ErrorSchema3,
      404: ErrorSchema3
    },
    summary: "The retrieval/citation units, in document order"
  },
  processingSteps: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/documents/:docId/processing-steps",
    responses: {
      200: z4.object({ steps: z4.array(ProcessingStepRowSchema) }),
      401: ErrorSchema3,
      404: ErrorSchema3
    },
    summary: "Every step of every processing run, oldest first"
  },
  // The same three-call upload the tenant contract uses — presign, direct PUT
  // to S3, complete — so an admin-uploaded document takes the identical path
  // into parsing and the Brain. The bytes never pass through the API.
  uploadUrl: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/documents/upload-url",
    body: UploadUrlInputSchema,
    responses: {
      201: UploadUrlResponseSchema,
      400: ErrorSchema3,
      401: ErrorSchema3,
      404: ErrorSchema3,
      413: ErrorSchema3
    },
    summary: "Reserve a document row and presign a direct-to-S3 PUT"
  },
  completeUpload: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/documents/:docId/complete",
    body: z4.object({}),
    responses: {
      200: z4.object({ document: ProjectDocumentSchema }),
      401: ErrorSchema3,
      404: ErrorSchema3,
      409: ErrorSchema3
    },
    summary: "Verify the object landed, then queue the quick pass: the card, then the full read"
  }
});

// src/projects.ts
var c4 = initContract4();
var AdminProjectCreateSchema = z5.object({
  basics: ProjectBasicsSchema,
  /** Requires `country` — activation's one real gate. Refused with a 400. */
  activate: z5.boolean().default(false)
});
var AdminFamilyMemberSchema = z5.object({
  id: z5.string().uuid(),
  /** The card's title, or the filename. */
  name: z5.string(),
  filename: z5.string(),
  role: FamilyRoleSchema,
  relatedDocumentId: z5.string().uuid().nullable(),
  madeBy: MadeBySchema.nullable(),
  reason: z5.string().nullable(),
  placedAt: z5.string().nullable(),
  documentDate: z5.string().nullable(),
  revision: z5.string().nullable(),
  /** The model's question, with the other file named, as the tenant's viewer asks it. */
  question: DocumentQuestionSchema.nullable()
});
var AdminFamilySchema = z5.object({
  id: z5.string().uuid(),
  name: z5.string().nullable(),
  series: z5.boolean(),
  madeBy: MadeBySchema,
  createdAt: z5.string(),
  members: z5.array(AdminFamilyMemberSchema)
});
var AdminFamiliesResponseSchema = z5.object({
  families: z5.array(AdminFamilySchema),
  /** Files on their own with a question waiting for a person. */
  questions: z5.array(AdminFamilyMemberSchema)
});
var AdminProjectChangeSchema = z5.object({
  id: z5.string().uuid(),
  kind: ProjectChangeKindSchema,
  documentId: z5.string().uuid().nullable(),
  /** The file's title or filename, when the change is about one file. */
  documentName: z5.string().nullable(),
  importId: z5.string().uuid().nullable(),
  madeBy: MadeBySchema,
  /** Null for a change the model, code or the backoffice made. */
  userId: z5.string().uuid().nullable(),
  undoesId: z5.string().uuid().nullable(),
  /** The entry that undid this one, when one did. */
  undoneById: z5.string().uuid().nullable(),
  before: z5.record(z5.unknown()).nullable(),
  after: z5.record(z5.unknown()).nullable(),
  createdAt: z5.string()
});
var AdminChangesResponseSchema = z5.object({
  changes: z5.array(AdminProjectChangeSchema),
  /**
   * The signal that the confidence bar is wrong (`document-ai/0016` §4,
   * `0022` §3): of the placements the model applied itself, how many a person
   * undid.
   */
  undoRate: z5.object({
    modelPlacements: z5.number().int().nonnegative(),
    undone: z5.number().int().nonnegative()
  })
});
var AdminLinksResponseSchema = z5.object({
  links: z5.array(DocumentLinkSchema),
  removals: z5.array(
    z5.object({
      kind: LinkKindSchema,
      foundBy: LinkFoundBySchema,
      made: z5.number().int().nonnegative(),
      /** Removed by a person, or answered No. A deleted file is not counted. */
      removed: z5.number().int().nonnegative()
    })
  )
});
var AdminBackfillStepSchema = z5.object({
  step: ProcessingStepSchema2,
  /** The version of the step's code now. */
  codeVersion: z5.number().int(),
  /** The oldest version among the latest records of the step. */
  oldestVersion: z5.number().int(),
  /** Files whose latest record is older than the code. Null for a step over the whole project. */
  files: z5.number().int().nonnegative().nullable(),
  /** The job queued per file, or null when the step is not redone file by file. */
  job: z5.enum(["repair-card", "repair-read"]).nullable(),
  /** What a backfill of it does, in words. */
  redoes: z5.string()
});
var AdminBackfillStartedSchema = z5.object({
  step: ProcessingStepSchema2,
  /** Jobs queued, one per file. */
  queued: z5.number().int().nonnegative(),
  /** Files left because a job of their own is still to run; it writes the step again. */
  waiting: z5.number().int().nonnegative()
});
var adminProjectsContract = c4.router({
  list: {
    method: "GET",
    path: "/tenants/:tenantId/projects",
    responses: {
      200: z5.object({ projects: z5.array(ProjectSchema) }),
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "Every project in the tenant, whatever its setup state"
  },
  get: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId",
    responses: {
      200: z5.object({ project: ProjectSchema }),
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "One project"
  },
  phases: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/phases",
    responses: {
      200: PhasesResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The project's phase plan"
  },
  /**
   * The brief (`document-ai/0017` §3). Read-only here. The project's
   * categories and their counts are in the admin Brain summary
   * (`document-ai/0032`); the outline this route also sent went on 2026-10-03.
   */
  brief: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/brief",
    responses: {
      200: ProjectBriefResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The project's brief, and who wrote it and when"
  },
  /** Every bundle that arrived, newest first, with counts read from the rows. */
  imports: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/imports",
    responses: {
      200: ImportsResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The project's imports, newest first"
  },
  /**
   * The steps that ran over a whole import — the project check, the brief,
   * the families and the links — which
   * no document's own step list shows, because they belong to no one file.
   */
  importSteps: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/imports/:importId/steps",
    responses: {
      200: z5.object({ steps: z5.array(ProcessingStepRowSchema) }),
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "Every step run over one import, oldest first"
  },
  /** Every family of two or more files, and every question waiting. */
  families: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/families",
    responses: {
      200: AdminFamiliesResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The project's families, with each member's role, reason and who placed it"
  },
  /** Every link, removed ones included, with the removal rate. */
  links: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/links",
    responses: {
      200: AdminLinksResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The project's links in every state, and how often each kind is removed"
  },
  /** The change log, newest first, with the undo rate. */
  changes: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/changes",
    responses: {
      200: AdminChangesResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "Every change to the project's organisation, newest first"
  },
  /**
   * The tenant's undo, from the backoffice (`architecture/0027`). The entry it
   * writes has no user: that is what "the backoffice did this" looks like.
   */
  undoChange: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/changes/:changeId/undo",
    body: c4.noBody(),
    responses: {
      200: UndoResponseSchema,
      401: ErrorSchema4,
      404: ErrorSchema4,
      409: ErrorSchema4
    },
    summary: "Undo a placement, as the tenant's own undo does"
  },
  /**
   * The whole processing queue for a project's documents — including
   * `attempts` and the user-safe `error`. The tenant contract only ever
   * exposes one job as an upload poll; support needs the queue.
   */
  jobs: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/jobs",
    responses: {
      200: z5.object({ jobs: z5.array(AdminJobSchema) }),
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The project's jobs, newest first"
  },
  /**
   * Each step whose code is newer than what it wrote for some of the project's
   * files (2026-09-26, RET-09): the files behind, and what a backfill redoes.
   */
  backfill: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/backfill",
    responses: {
      200: z5.object({ steps: z5.array(AdminBackfillStepSchema) }),
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "The steps whose code has changed since they ran on the project's files"
  },
  /**
   * Backfill one step: the smallest job per file that is behind. A person
   * starts it; nothing starts it after a deploy (the default of 2026-09-26,
   * which the owner can change).
   */
  startBackfill: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/backfill",
    body: z5.object({ step: ProcessingStepSchema2 }),
    responses: {
      202: AdminBackfillStartedSchema,
      400: ErrorSchema4,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "Redo one step on the files it ran on with older code"
  },
  create: {
    method: "POST",
    path: "/tenants/:tenantId/projects",
    body: AdminProjectCreateSchema,
    responses: {
      201: z5.object({ project: ProjectSchema }),
      400: ErrorSchema4,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "Create a site in one window \u2014 the wizard's basics, optionally activated"
  },
  setPhases: {
    method: "PUT",
    path: "/tenants/:tenantId/projects/:projectId/phases",
    // The tenant wizard's bulk replace, unchanged: position comes from array
    // order, ids are kept where sent, and a mid-array failure applies nothing.
    body: PhasesInputSchema,
    responses: {
      200: PhasesResponseSchema,
      400: ErrorSchema4,
      401: ErrorSchema4,
      404: ErrorSchema4
    },
    summary: "Replace the project's phase plan"
  }
});

// src/brain.ts
import { initContract as initContract5 } from "@ts-rest/core";
import { z as z6 } from "zod";
import { FileStatusSchema } from "@gb/contracts";
import {
  BrainItemSchema,
  ErrorSchema as ErrorSchema5,
  KnowledgeEntrySchema,
  KnowledgeNoteInputSchema,
  KnowledgeSourceTypeSchema,
  KnowledgeSummarySchema
} from "@gb/contracts";
var c5 = initContract5();
var adminBrainContract = c5.router({
  summary: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/brain",
    responses: {
      200: KnowledgeSummarySchema,
      401: ErrorSchema5,
      404: ErrorSchema5
    },
    summary: "What this site's Brain holds, counted by source"
  },
  items: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/brain/items",
    // Same query the tenant route takes, coercion included — see the long
    // comment on the tenant contract's `list` for why the coercion matters.
    query: z6.object({
      q: z6.string().trim().max(200).optional(),
      sourceType: KnowledgeSourceTypeSchema.optional(),
      category: z6.string().trim().min(1).max(80).optional(),
      relevant: z6.string().trim().min(1).max(80).optional(),
      sorted: z6.literal("no").optional(),
      decision: z6.literal("open").optional(),
      status: FileStatusSchema.optional(),
      organised: z6.literal("no").optional(),
      /** The "Removed at source" shelf, as the tenant query has it (2026-10-18, drift W-069). */
      removed: z6.literal("yes").optional(),
      limit: z6.coerce.number().int().min(1).max(100).default(50),
      offset: z6.coerce.number().int().min(0).default(0)
    }),
    responses: {
      200: z6.object({
        items: z6.array(BrainItemSchema),
        total: z6.number().int()
      }),
      400: ErrorSchema5,
      401: ErrorSchema5,
      404: ErrorSchema5
    },
    summary: "Browse and filter: files and entries, never chunks"
  },
  getEntry: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/brain/entries/:entryId",
    responses: {
      200: z6.object({ entry: KnowledgeEntrySchema }),
      401: ErrorSchema5,
      404: ErrorSchema5
    },
    summary: "One entry, with its full body"
  },
  createEntry: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/brain/entries",
    body: KnowledgeNoteInputSchema,
    responses: {
      201: z6.object({ entry: KnowledgeEntrySchema }),
      400: ErrorSchema5,
      401: ErrorSchema5,
      404: ErrorSchema5
    },
    summary: "Add a hand-written note to this site's Brain"
  },
  deleteEntry: {
    method: "DELETE",
    path: "/tenants/:tenantId/projects/:projectId/brain/entries/:entryId",
    body: c5.noBody(),
    responses: {
      204: c5.noBody(),
      401: ErrorSchema5,
      404: ErrorSchema5,
      // Same rule as the tenant route: a `project-doc` row is owned by the
      // ingest path and would re-create itself — delete the document instead.
      409: ErrorSchema5
    },
    summary: "Delete a note. Only `upload` entries can be deleted here"
  }
});

// src/journals.ts
import { initContract as initContract6 } from "@ts-rest/core";
import { z as z7 } from "zod";
import {
  ErrorSchema as ErrorSchema6,
  IsoDateSchema,
  JournalApproveSchema,
  JournalDaysResponseSchema,
  JournalDictationSchema,
  JournalEntryInputSchema,
  JournalEntryPatchSchema,
  JournalEntryResponseSchema,
  JournalNotesResponseSchema,
  JournalPhotoResponseSchema,
  JournalPhotosResponseSchema,
  PhotoPatchSchema,
  PhotoUploadUrlInputSchema,
  PhotoUploadUrlResponseSchema
} from "@gb/contracts";
var c6 = initContract6();
var adminJournalsContract = c6.router({
  days: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/journal/days",
    query: z7.object({ from: IsoDateSchema.optional(), to: IsoDateSchema.optional() }),
    responses: {
      200: JournalDaysResponseSchema,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "The journal's day summaries"
  },
  entry: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/journal/days/:date",
    responses: {
      200: JournalEntryResponseSchema,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "One day's entry"
  },
  notes: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/journal/days/:date/notes",
    responses: {
      200: JournalNotesResponseSchema,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "The day's captured notes, with their extraction state"
  },
  photos: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/journal/days/:date/photos",
    responses: {
      200: JournalPhotosResponseSchema,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "The day's photos, with their vision output"
  },
  dictations: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/dictations",
    query: AdminPageQuerySchema,
    responses: {
      200: z7.object({
        dictations: z7.array(JournalDictationSchema),
        total: z7.number().int().min(0)
      }),
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "The project's dictations \u2014 transcript, suggestion, statuses \u2014 newest first"
  },
  createEntry: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/journal",
    body: JournalEntryInputSchema,
    responses: {
      201: JournalEntryResponseSchema,
      400: ErrorSchema6,
      401: ErrorSchema6,
      404: ErrorSchema6,
      409: ErrorSchema6
    },
    summary: "Open a day. Only `date` is required. 409 if that day already has one"
  },
  patchEntry: {
    method: "PATCH",
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId",
    body: JournalEntryPatchSchema,
    responses: {
      200: JournalEntryResponseSchema,
      400: ErrorSchema6,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "Amend a day's record. The date itself is not editable"
  },
  approveEntry: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId/approve",
    body: JournalApproveSchema,
    responses: {
      // Same gate as the tenant route: an incomplete day is refused, via the
      // shared `missingForSignOff`.
      200: JournalEntryResponseSchema,
      400: ErrorSchema6,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "Sign off a *complete* day's record and record who it went to"
  },
  deleteEntry: {
    method: "DELETE",
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId",
    body: c6.noBody(),
    responses: {
      204: c6.noBody(),
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "Delete a day's record, and every photograph on it"
  },
  photoUploadUrl: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId/photos/upload-url",
    body: PhotoUploadUrlInputSchema,
    responses: {
      201: PhotoUploadUrlResponseSchema,
      400: ErrorSchema6,
      401: ErrorSchema6,
      404: ErrorSchema6,
      413: ErrorSchema6
    },
    summary: "Reserve a photo row and presign the PUTs"
  },
  completePhoto: {
    method: "POST",
    // `{}` only — the multipart finish the tenant route also accepts belongs
    // to the phone's resumable path, which is not mirrored here.
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId/photos/:photoId/complete",
    body: z7.object({}),
    responses: {
      200: JournalPhotoResponseSchema,
      401: ErrorSchema6,
      404: ErrorSchema6,
      409: ErrorSchema6
    },
    summary: "Verify the bytes landed, then describe the photo out of band"
  },
  patchPhoto: {
    method: "PATCH",
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId/photos/:photoId",
    body: PhotoPatchSchema,
    responses: {
      200: JournalPhotoResponseSchema,
      400: ErrorSchema6,
      401: ErrorSchema6,
      404: ErrorSchema6
    },
    summary: "Caption a photo. The model's own fields are not writable"
  },
  deletePhoto: {
    method: "DELETE",
    path: "/tenants/:tenantId/projects/:projectId/journal/:entryId/photos/:photoId",
    body: c6.noBody(),
    responses: { 204: c6.noBody(), 401: ErrorSchema6, 404: ErrorSchema6 },
    summary: "Remove a photograph, bytes included"
  }
});

// src/chat.ts
import { initContract as initContract7 } from "@ts-rest/core";
import { z as z8 } from "zod";
import {
  AnswerConfidenceSchema,
  AnswerStepSchema,
  ChatMessageSchema,
  ChatThreadInputSchema,
  ChatThreadSchema,
  ChatThreadSummarySchema,
  ErrorSchema as ErrorSchema7,
  LookTargetSchema
} from "@gb/contracts";
var c7 = initContract7();
var AdminChatThreadSummarySchema = ChatThreadSummarySchema.extend({
  sharedWithTenant: z8.boolean()
});
var AdminAnswerStepSchema = AnswerStepSchema.extend({
  input: z8.unknown(),
  output: z8.string().nullable(),
  outputChars: z8.number().int().min(0),
  /** The line in en-GB as the server stored it, for the logs. */
  line: z8.string(),
  /** The run of the model turn or look behind the row (`runs/run-detail-page.tsx`). */
  runId: z8.string().uuid().nullable(),
  lookId: z8.string().uuid().nullable(),
  /** The API's refusal category, when a turn was refused (§1.2). Null when it gave none. */
  refusal: z8.string().nullable()
});
var AdminAnswerRunSchema = z8.object({
  id: z8.string().uuid(),
  touchpoint: z8.string(),
  model: z8.string(),
  /** `ok`, `refusal`, `max_tokens`, `schema_miss`, `error` or `skipped`. */
  status: z8.string(),
  latencyMs: z8.number().int(),
  /** Tokens, as the provider counted them. Nothing here is priced. */
  inputTokens: z8.number().int().nullable(),
  outputTokens: z8.number().int().nullable(),
  cacheReadTokens: z8.number().int().nullable(),
  cacheWriteTokens: z8.number().int().nullable(),
  /** Pictures sent. 0 is a call with none; null is a call recorded before they were counted. */
  imageCount: z8.number().int().nullable(),
  /** The answer the call belongs to (its `chat_messages.id`). Null for a call recorded before 2026-09-26. */
  answerId: z8.string().uuid().nullable(),
  /** The agent turn, counted from 1. Null for a look, the review and the naming call. */
  turn: z8.number().int().nullable(),
  /** Class and HTTP status of a failed call. Null when it did not fail. */
  errorClass: z8.string().nullable(),
  /** The provider's own message, capped and redacted when it was recorded (`architecture/0030`). */
  errorMessage: z8.string().nullable(),
  createdAt: z8.string()
});
var AdminAnswerRunsSchema = z8.object({ runs: z8.array(AdminAnswerRunSchema), more: z8.boolean() });
var AdminAnswerCountsSchema = z8.object({
  answers: z8.number().int().min(0),
  /** By `AnswerEndedBy` value, as stored; `answer` is the answers that finished. */
  endedBy: z8.record(z8.string(), z8.number().int().min(0)),
  /** Citation markers dropped because the registry had no such number (R93). */
  refusedCitations: z8.number().int().min(0),
  /** By level; `none` is an answer nothing assessed. */
  levels: z8.object({
    high: z8.number().int().min(0),
    partial: z8.number().int().min(0),
    "needs-confirmation": z8.number().int().min(0),
    none: z8.number().int().min(0)
  })
});
var AdminKeptLookSchema = z8.object({
  id: z8.string().uuid(),
  projectId: z8.string().uuid(),
  target: LookTargetSchema,
  question: z8.string(),
  topic: z8.string().nullable(),
  model: z8.string(),
  /** The answer that made the look. */
  answerId: z8.string().uuid().nullable(),
  /** The answer marked wrong that made or read it, and when it was marked. */
  markedAnswerId: z8.string().uuid(),
  markedWrongAt: z8.string(),
  confidence: AnswerConfidenceSchema.nullable(),
  createdAt: z8.string()
});
var adminChatContract = c7.router({
  threads: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads",
    query: AdminPageQuerySchema,
    responses: {
      200: z8.object({
        threads: z8.array(AdminChatThreadSummarySchema),
        total: z8.number().int().min(0)
      }),
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Questions asked of this project's brain, newest first"
  },
  messages: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads/:threadId/messages",
    responses: {
      200: z8.object({ messages: z8.array(ChatMessageSchema) }),
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "One thread's turns, with sources and confidence"
  },
  createThread: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads",
    body: ChatThreadInputSchema,
    responses: {
      201: z8.object({ thread: ChatThreadSchema }),
      400: ErrorSchema7,
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Open an admin thread \u2014 hidden from the tenant until promoted"
  },
  setThreadVisibility: {
    method: "PATCH",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads/:threadId/visibility",
    // Both directions, by owner decision (2026-08-28, superseding the one-way
    // `promote` this route replaces): an admin may unshare a thread they
    // shared. The cost that made v2 refuse this — a thread the tenant has
    // seen vanishing from their history — is now accepted as the admin's
    // call, and the UI confirms before either flip.
    body: z8.object({ sharedWithTenant: z8.boolean() }),
    responses: {
      200: z8.object({ thread: ChatThreadSchema }),
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Show or hide an admin thread to the tenant's users. Idempotent"
  },
  // ---- The answering agent (Phase 5, 2026-09-26).
  listSteps: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads/:threadId/steps",
    responses: {
      200: z8.object({ steps: z8.array(AdminAnswerStepSchema) }),
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Every step of the thread's answer, with its input and its output whole"
  },
  listRuns: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads/:threadId/runs",
    responses: {
      200: AdminAnswerRunsSchema,
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Every model call of the thread's answer, oldest first, without its bodies"
  },
  answerCounts: {
    method: "GET",
    path: "/tenants/:tenantId/chat/answer-counts",
    query: z8.object({
      projectId: z8.string().uuid().optional(),
      from: z8.string().optional(),
      to: z8.string().optional()
    }),
    responses: {
      200: AdminAnswerCountsSchema,
      400: ErrorSchema7,
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "How the tenant's answers ended: step limits, early ends, refused citations, levels"
  },
  // A write: it stops an answer. The admin twin of the tenant's `cancelAsk`,
  // because Stop and the next question on the backoffice stop a running answer
  // through the database too (§1.11 of the Phase 5 design).
  cancelAsk: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/chat/threads/:threadId/cancel",
    body: c7.noBody(),
    responses: {
      200: z8.object({ requested: z8.boolean() }),
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Stop the answer that is running on this thread"
  },
  listLooksMarkedWrong: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/chat/looks-marked-wrong",
    query: AdminPageQuerySchema,
    responses: {
      200: z8.object({
        looks: z8.array(AdminKeptLookSchema),
        total: z8.number().int().min(0)
      }),
      401: ErrorSchema7,
      404: ErrorSchema7
    },
    summary: "Kept looks that answers marked as wrong made or read, newest first"
  }
});

// src/crew.ts
import { initContract as initContract8 } from "@ts-rest/core";
import { z as z9 } from "zod";
import {
  AddCrewMemberInputSchema,
  CertificateSuggestionSchema,
  ConfirmCertificateInputSchema,
  CrewCertificateResponseSchema,
  CrewCertificateSchema,
  CrewMemberResponseSchema,
  CrewRosterSchema,
  CrewUploadUrlInputSchema,
  CrewUploadUrlResponseSchema,
  ErrorSchema as ErrorSchema8
} from "@gb/contracts";
var c8 = initContract8();
var adminCrewContract = c8.router({
  roster: {
    method: "GET",
    path: "/tenants/:tenantId/projects/:projectId/crew",
    responses: {
      200: CrewRosterSchema,
      401: ErrorSchema8,
      404: ErrorSchema8
    },
    summary: "The project's roster with compliance state, as the console shows it"
  },
  certificates: {
    method: "GET",
    path: "/tenants/:tenantId/crew/:personId/certificates",
    responses: {
      200: z9.object({ certificates: z9.array(CrewCertificateSchema) }),
      401: ErrorSchema8,
      404: ErrorSchema8
    },
    summary: "A person's documents, as the dashboard roster sees them"
  },
  addMember: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/crew",
    body: AddCrewMemberInputSchema,
    responses: {
      201: CrewMemberResponseSchema,
      400: ErrorSchema8,
      401: ErrorSchema8,
      404: ErrorSchema8,
      409: ErrorSchema8
    },
    summary: "Create a person and open their first period on this project"
  },
  offboard: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/crew/:personId/offboard",
    body: z9.object({}),
    responses: {
      200: CrewMemberResponseSchema,
      401: ErrorSchema8,
      404: ErrorSchema8,
      409: ErrorSchema8
    },
    summary: "Close the open period. The person and their documents remain"
  },
  reonboard: {
    method: "POST",
    path: "/tenants/:tenantId/projects/:projectId/crew/:personId/reonboard",
    body: z9.object({}),
    responses: {
      200: CrewMemberResponseSchema,
      401: ErrorSchema8,
      404: ErrorSchema8,
      409: ErrorSchema8
    },
    summary: "Open a new period on this project"
  },
  certUploadUrl: {
    method: "POST",
    path: "/tenants/:tenantId/crew/:personId/documents/upload-url",
    body: CrewUploadUrlInputSchema,
    responses: {
      201: CrewUploadUrlResponseSchema,
      400: ErrorSchema8,
      401: ErrorSchema8,
      404: ErrorSchema8,
      413: ErrorSchema8
    },
    summary: "Reserve an unconfirmed certificate row and presign a PUT to S3"
  },
  completeCertUpload: {
    method: "POST",
    path: "/tenants/:tenantId/crew/:personId/documents/:docId/complete",
    body: z9.object({}),
    responses: {
      200: z9.object({
        certificate: CrewCertificateSchema,
        suggestion: CertificateSuggestionSchema
      }),
      401: ErrorSchema8,
      404: ErrorSchema8,
      409: ErrorSchema8
    },
    summary: "Verify the object landed and read the card \u2014 suggestions only, nothing held"
  },
  confirmCertificate: {
    method: "PUT",
    path: "/tenants/:tenantId/crew/:personId/documents/:docId",
    body: ConfirmCertificateInputSchema,
    responses: {
      200: CrewCertificateResponseSchema,
      400: ErrorSchema8,
      401: ErrorSchema8,
      404: ErrorSchema8,
      409: ErrorSchema8
    },
    summary: "Accept the fields. The certificate now counts towards compliance"
  },
  deleteCertificate: {
    method: "DELETE",
    path: "/tenants/:tenantId/crew/:personId/documents/:docId",
    body: c8.noBody(),
    responses: { 204: c8.noBody(), 401: ErrorSchema8, 404: ErrorSchema8 },
    summary: "Discard an upload, or remove a certificate that is on file"
  }
});

// src/prompts.ts
import { initContract as initContract9 } from "@ts-rest/core";
import { z as z11 } from "zod";
import { ErrorSchema as ErrorSchema9 } from "@gb/contracts";

// src/llm.ts
import { z as z10 } from "zod";
import {
  LlmParamsSchema,
  LlmProviderSchema,
  LlmRunImageSchema,
  LlmRunStatusSchema,
  LlmTouchpointSlugSchema
} from "@gb/contracts";
import {
  LLM_MAX_TOKENS_CEILING,
  LlmEffortSchema,
  LlmParamsSchema as LlmParamsSchema2,
  LlmProviderSchema as LlmProviderSchema2,
  LlmRunStatusSchema as LlmRunStatusSchema2,
  LlmTouchpointSlugSchema as LlmTouchpointSlugSchema2
} from "@gb/contracts";
var PromptVariableSchema = z10.object({
  required: z10.boolean(),
  description: z10.string(),
  example: z10.string()
});
var PromptVersionSchema = z10.object({
  id: z10.string().uuid().nullable(),
  touchpointSlug: LlmTouchpointSlugSchema,
  version: z10.number().int().nonnegative(),
  body: z10.string(),
  params: LlmParamsSchema,
  isActive: z10.boolean(),
  note: z10.string().nullable(),
  /** Admin email, or "code" for version 0. Text, not an FK — provenance, not a join. */
  createdBy: z10.string(),
  createdAt: z10.string().nullable(),
  /**
   * Written from an older code prompt than this build ships (2026-10-03,
   * `architecture/0020`). The registry does not use it, active or not, and it
   * cannot be switched on; saving it again makes a current version. Always
   * false for version 0.
   */
  outOfDate: z10.boolean()
});
var PromptVersionCreateSchema = z10.object({
  body: z10.string().min(1).max(1e5),
  params: LlmParamsSchema.default({}),
  note: z10.string().max(2e3).optional()
});
var PromptActivateInputSchema = z10.object({
  versionId: z10.string().uuid().nullable()
});
var PromptPreviewInputSchema = z10.object({
  body: z10.string().min(1).max(1e5),
  params: LlmParamsSchema.optional()
});
var PromptPreviewResponseSchema = z10.object({
  rendered: z10.string(),
  issues: z10.array(z10.string())
});
var TouchpointSummarySchema = z10.object({
  slug: LlmTouchpointSlugSchema,
  name: z10.string(),
  description: z10.string(),
  area: z10.string(),
  provider: LlmProviderSchema,
  /** Wire params after precedence (active version ?? code default ?? seam default). */
  effectiveModel: z10.string(),
  /** The version that runs: 0 when none is switched on, or the one that is is out of date. */
  activeVersion: z10.number().int().nonnegative(),
  /** A version switched on but written from an older code prompt, so not used. */
  outOfDateVersion: z10.number().int().positive().nullable(),
  versionCount: z10.number().int().nonnegative()
});
var TouchpointDetailSchema = TouchpointSummarySchema.extend({
  variables: z10.record(PromptVariableSchema),
  capabilities: z10.object({
    /** Whether `params.temperature` is legal for this touchpoint's provider. */
    temperature: z10.boolean(),
    streaming: z10.boolean()
  }),
  /** The version-0 body and params, shown beside any override for diffing. */
  codeDefault: z10.object({
    body: z10.string(),
    params: LlmParamsSchema
  }),
  /** Code-owned suffix (injection hardening, fencing). Read-only by design. */
  guard: z10.string()
});
var PromptScopeSchema = z10.enum(["tenant", "platform", "code"]);
var TenantTouchpointSummarySchema = z10.object({
  slug: LlmTouchpointSlugSchema,
  name: z10.string(),
  description: z10.string(),
  area: z10.string(),
  provider: LlmProviderSchema,
  /** After the full ladder: tenant params ← platform params ← code ← seam. */
  effectiveModel: z10.string(),
  /** Which layer's body the tenant resolves to right now. */
  effectiveScope: PromptScopeSchema,
  /** The tenant's and the platform's versions that run; null when none does. */
  tenantActiveVersion: z10.number().int().positive().nullable(),
  platformActiveVersion: z10.number().int().positive().nullable(),
  /** Versions switched on but written from an older code prompt, so not used (2026-10-03). */
  tenantOutOfDateVersion: z10.number().int().positive().nullable(),
  platformOutOfDateVersion: z10.number().int().positive().nullable(),
  tenantVersionCount: z10.number().int().nonnegative()
});
var TenantTouchpointDetailSchema = TenantTouchpointSummarySchema.extend({
  variables: z10.record(PromptVariableSchema),
  capabilities: z10.object({
    temperature: z10.boolean(),
    streaming: z10.boolean()
  }),
  codeDefault: z10.object({
    body: z10.string(),
    params: LlmParamsSchema
  }),
  guard: z10.string(),
  /** What the tenant inherits when it has no active row of its own. */
  platformActive: PromptVersionSchema.nullable(),
  tenantActive: PromptVersionSchema.nullable()
});
var LlmRunSummarySchema = z10.object({
  id: z10.string().uuid(),
  /** Which tenant's schema the row lives in — the read is a cross-schema union. */
  tenantSlug: z10.string(),
  touchpoint: z10.string(),
  provider: LlmProviderSchema,
  model: z10.string(),
  promptVersion: z10.number().int().nonnegative(),
  /** Which table served the prompt; null = code default (matches `promptVersion` 0). */
  promptScope: z10.enum(["tenant", "platform"]).nullable(),
  projectId: z10.string().uuid().nullable(),
  subjectKind: z10.string().nullable(),
  subjectId: z10.string().uuid().nullable(),
  status: LlmRunStatusSchema,
  errorClass: z10.string().nullable(),
  latencyMs: z10.number().int().nonnegative(),
  inputTokens: z10.number().int().nullable(),
  outputTokens: z10.number().int().nullable(),
  createdAt: z10.string()
});
var LlmRunDetailSchema = LlmRunSummarySchema.extend({
  promptVersionId: z10.string().uuid().nullable(),
  /** The user prompt as sent, capped at 200k chars at write time (20k before
   *  2026-09-25). */
  promptText: z10.string().nullable(),
  /**
   * The system prompt as sent, kept for every call since 2026-09-25
   * (`architecture/0029` §3). Null for a call recorded before that. Added
   * here 2026-09-27, so the thread page can show a call whole when it is
   * opened.
   */
  systemText: z10.string().nullable(),
  /**
   * The model's summarised thinking, where it returned any. An agent turn's
   * holds the model's notes between tool calls (`architecture/0029` §3).
   */
  thinking: z10.string().nullable(),
  /**
   * The provider's own message when the call failed, capped and redacted at
   * write time (`architecture/0030`). `errorClass` stays class and status.
   */
  errorMessage: z10.string().nullable(),
  imageCount: z10.number().int().nullable(),
  /** Each picture as stored and as sent (`document-ai/0029`). */
  images: z10.array(LlmRunImageSchema).nullable(),
  output: z10.unknown().nullable(),
  cacheReadTokens: z10.number().int().nullable(),
  cacheWriteTokens: z10.number().int().nullable()
});
var LlmRunListQuerySchema = z10.object({
  /** Absent ⇒ union across every registered tenant. */
  tenant: z10.string().optional(),
  touchpoint: LlmTouchpointSlugSchema.optional(),
  status: LlmRunStatusSchema.optional(),
  projectId: z10.string().uuid().optional(),
  from: z10.string().optional(),
  to: z10.string().optional(),
  limit: z10.coerce.number().int().min(1).max(100).default(50),
  offset: z10.coerce.number().int().min(0).default(0)
});
var LlmRunStatsSchema = z10.object({
  touchpoint: z10.string(),
  count: z10.number().int().nonnegative(),
  errorCount: z10.number().int().nonnegative(),
  p50LatencyMs: z10.number().nullable(),
  totalInputTokens: z10.number().int().nonnegative(),
  totalOutputTokens: z10.number().int().nonnegative()
});

// src/prompts.ts
var c9 = initContract9();
var adminPromptsContract = c9.router({
  listTouchpoints: {
    method: "GET",
    path: "/prompts",
    responses: {
      200: z11.object({ touchpoints: z11.array(TouchpointSummarySchema) }),
      401: ErrorSchema9
    },
    summary: "Every model call site, with its effective model and active version"
  },
  getTouchpoint: {
    method: "GET",
    path: "/prompts/:slug",
    pathParams: z11.object({ slug: LlmTouchpointSlugSchema2 }),
    responses: {
      200: TouchpointDetailSchema,
      401: ErrorSchema9,
      404: ErrorSchema9
    },
    summary: "One touchpoint: variables, capabilities, code default, guard"
  },
  listVersions: {
    method: "GET",
    path: "/prompts/:slug/versions",
    pathParams: z11.object({ slug: LlmTouchpointSlugSchema2 }),
    responses: {
      200: z11.object({ versions: z11.array(PromptVersionSchema) }),
      401: ErrorSchema9,
      404: ErrorSchema9
    },
    summary: "The touchpoint's versions, newest first, version 0 synthesised last"
  },
  createVersion: {
    method: "POST",
    path: "/prompts/:slug/versions",
    pathParams: z11.object({ slug: LlmTouchpointSlugSchema2 }),
    body: PromptVersionCreateSchema,
    responses: {
      201: z11.object({ version: PromptVersionSchema }),
      // Unknown placeholder, missing required placeholder, or a param the
      // touchpoint's provider cannot take (temperature on Anthropic).
      400: ErrorSchema9,
      401: ErrorSchema9,
      404: ErrorSchema9
    },
    summary: "Save a new immutable version (does not activate it)"
  },
  activate: {
    method: "POST",
    path: "/prompts/:slug/activate",
    pathParams: z11.object({ slug: LlmTouchpointSlugSchema2 }),
    body: PromptActivateInputSchema,
    responses: {
      200: z11.object({ success: z11.literal(true) }),
      401: ErrorSchema9,
      404: ErrorSchema9
    },
    summary: "Make one version live, or revert to the code default with null"
  },
  preview: {
    method: "POST",
    path: "/prompts/:slug/preview",
    pathParams: z11.object({ slug: LlmTouchpointSlugSchema2 }),
    body: PromptPreviewInputSchema,
    responses: {
      200: PromptPreviewResponseSchema,
      401: ErrorSchema9,
      404: ErrorSchema9
    },
    summary: "Render a draft body with example variables and the guard appended"
  },
  // Tenant-level overrides: same immutable-version semantics against the
  // tenant's own `llm_prompt_versions` table. `activate` with null does not
  // mean "code default" here — it means "inherit the platform ladder again".
  // Nested under `prompts.` so the write-surface allowlist stays two-entry.
  tenant: c9.router({
    listTouchpoints: {
      method: "GET",
      path: "/tenants/:tenantId/prompts",
      pathParams: z11.object({ tenantId: z11.string() }),
      responses: {
        200: z11.object({ touchpoints: z11.array(TenantTouchpointSummarySchema) }),
        401: ErrorSchema9,
        404: ErrorSchema9
      },
      summary: "Every touchpoint with what this tenant actually resolves to, and from which layer"
    },
    getTouchpoint: {
      method: "GET",
      path: "/tenants/:tenantId/prompts/:slug",
      pathParams: z11.object({ tenantId: z11.string(), slug: LlmTouchpointSlugSchema2 }),
      responses: {
        200: TenantTouchpointDetailSchema,
        401: ErrorSchema9,
        404: ErrorSchema9
      },
      summary: "One touchpoint through this tenant's eyes: tenant active, platform active, code default"
    },
    listVersions: {
      method: "GET",
      path: "/tenants/:tenantId/prompts/:slug/versions",
      pathParams: z11.object({ tenantId: z11.string(), slug: LlmTouchpointSlugSchema2 }),
      responses: {
        200: z11.object({ versions: z11.array(PromptVersionSchema) }),
        401: ErrorSchema9,
        404: ErrorSchema9
      },
      summary: "This tenant's own versions, newest first (inheritance is shown on the detail)"
    },
    createVersion: {
      method: "POST",
      path: "/tenants/:tenantId/prompts/:slug/versions",
      pathParams: z11.object({ tenantId: z11.string(), slug: LlmTouchpointSlugSchema2 }),
      body: PromptVersionCreateSchema,
      responses: {
        201: z11.object({ version: PromptVersionSchema }),
        400: ErrorSchema9,
        401: ErrorSchema9,
        404: ErrorSchema9
      },
      summary: "Save a new immutable tenant version (does not activate it)"
    },
    activate: {
      method: "POST",
      path: "/tenants/:tenantId/prompts/:slug/activate",
      pathParams: z11.object({ tenantId: z11.string(), slug: LlmTouchpointSlugSchema2 }),
      body: PromptActivateInputSchema,
      responses: {
        200: z11.object({ success: z11.literal(true) }),
        401: ErrorSchema9,
        404: ErrorSchema9
      },
      summary: "Make one tenant version live, or null to inherit the platform ladder again"
    }
  })
});

// src/runs.ts
import { initContract as initContract10 } from "@ts-rest/core";
import { z as z12 } from "zod";
import { ErrorSchema as ErrorSchema10 } from "@gb/contracts";
var c10 = initContract10();
var adminRunsContract = c10.router({
  list: {
    method: "GET",
    path: "/runs",
    query: LlmRunListQuerySchema,
    responses: {
      200: z12.object({
        runs: z12.array(LlmRunSummarySchema),
        total: z12.number().int().min(0)
      }),
      401: ErrorSchema10
    },
    summary: "Model invocations, newest first, across tenants unless filtered"
  },
  stats: {
    method: "GET",
    path: "/runs/stats",
    query: z12.object({
      tenant: z12.string().optional(),
      from: z12.string().optional(),
      to: z12.string().optional()
    }),
    responses: {
      200: z12.object({ stats: z12.array(LlmRunStatsSchema) }),
      401: ErrorSchema10
    },
    summary: "Per-touchpoint volume, errors, latency and token totals"
  },
  get: {
    method: "GET",
    path: "/runs/:tenantSlug/:runId",
    responses: {
      200: LlmRunDetailSchema,
      401: ErrorSchema10,
      404: ErrorSchema10
    },
    summary: "One invocation: prompt sent, output, usage, version used"
  }
});

// src/paths.ts
var ADMIN_OFF_CONTRACT_ROUTES = {
  chatAsk: "admin/tenants/:tenantId/projects/:projectId/chat/threads/:threadId/ask",
  documentDownload: "admin/tenants/:tenantId/projects/:projectId/documents/:docId/download",
  documentPreview: "admin/tenants/:tenantId/projects/:projectId/documents/:docId/preview",
  documentAssetContent: "admin/tenants/:tenantId/projects/:projectId/documents/:docId/assets/:assetId/content",
  journalPhotoContent: "admin/tenants/:tenantId/projects/:projectId/journal/:entryId/photos/:photoId/content",
  journalPhotoThumbnail: "admin/tenants/:tenantId/projects/:projectId/journal/:entryId/photos/:photoId/thumbnail",
  crewDocumentDownload: "admin/tenants/:tenantId/crew/:personId/documents/:docId/download"
};
var seg = (value) => encodeURIComponent(value);
function adminChatAskPath(tenantId, projectId, threadId) {
  return `/admin/tenants/${seg(tenantId)}/projects/${seg(projectId)}/chat/threads/${seg(threadId)}/ask`;
}
var doc = (tenantId, projectId, documentId) => `/admin/tenants/${seg(tenantId)}/projects/${seg(projectId)}/documents/${seg(documentId)}`;
function adminDocumentDownloadPath(tenantId, projectId, documentId) {
  return `${doc(tenantId, projectId, documentId)}/download`;
}
function adminDocumentPreviewPath(tenantId, projectId, documentId) {
  return `${doc(tenantId, projectId, documentId)}/preview`;
}
function adminDocumentAssetContentPath(tenantId, projectId, documentId, assetId) {
  return `${doc(tenantId, projectId, documentId)}/assets/${seg(assetId)}/content`;
}
var photo = (tenantId, projectId, entryId, photoId) => `/admin/tenants/${seg(tenantId)}/projects/${seg(projectId)}/journal/${seg(entryId)}/photos/${seg(photoId)}`;
function adminJournalPhotoContentPath(tenantId, projectId, entryId, photoId) {
  return `${photo(tenantId, projectId, entryId, photoId)}/content`;
}
function adminJournalPhotoThumbnailPath(tenantId, projectId, entryId, photoId) {
  return `${photo(tenantId, projectId, entryId, photoId)}/thumbnail`;
}
function adminCrewDocumentDownloadPath(tenantId, personId, documentId) {
  return `/admin/tenants/${seg(tenantId)}/crew/${seg(personId)}/documents/${seg(documentId)}/download`;
}

// src/index.ts
var c11 = initContract11();
var adminContract = c11.router(
  {
    auth: adminAuthContract,
    tenants: adminTenantsContract,
    projects: adminProjectsContract,
    documents: adminDocumentsContract,
    brain: adminBrainContract,
    journals: adminJournalsContract,
    chat: adminChatContract,
    crew: adminCrewContract,
    prompts: adminPromptsContract,
    runs: adminRunsContract
  },
  {
    pathPrefix: "/admin",
    strictStatusCodes: true
  }
);
export {
  ADMIN_OFF_CONTRACT_ROUTES,
  AdminAnswerCountsSchema,
  AdminAnswerRunSchema,
  AdminAnswerRunsSchema,
  AdminAnswerStepSchema,
  AdminBackfillStartedSchema,
  AdminBackfillStepSchema,
  AdminChangesResponseSchema,
  AdminChatThreadSummarySchema,
  AdminFamiliesResponseSchema,
  AdminFamilyMemberSchema,
  AdminFamilySchema,
  AdminJobSchema,
  AdminKeptLookSchema,
  AdminLinksResponseSchema,
  AdminMeResponseSchema,
  AdminPageQuerySchema,
  AdminProjectChangeSchema,
  AdminProjectCreateSchema,
  AdminRefreshInputSchema,
  AdminSessionSchema,
  AdminTenantCreateSchema,
  AdminTenantSchema,
  AdminTenantStatsSchema,
  AdminTenantStatusSchema,
  AdminTenantUserCreateSchema,
  AdminTenantUserSchema,
  AdminTokensResponseSchema,
  AdminUserSchema,
  AdminUserStatusSchema2 as AdminUserStatusSchema,
  GoogleAuthUrlResponseSchema,
  GoogleCallbackInputSchema,
  LLM_MAX_TOKENS_CEILING,
  LlmEffortSchema,
  LlmParamsSchema2 as LlmParamsSchema,
  LlmProviderSchema2 as LlmProviderSchema,
  LlmRunDetailSchema,
  LlmRunListQuerySchema,
  LlmRunStatsSchema,
  LlmRunStatusSchema2 as LlmRunStatusSchema,
  LlmRunSummarySchema,
  LlmTouchpointSlugSchema2 as LlmTouchpointSlugSchema,
  ProcessingStepRowSchema,
  PromptActivateInputSchema,
  PromptPreviewInputSchema,
  PromptPreviewResponseSchema,
  PromptScopeSchema,
  PromptVariableSchema,
  PromptVersionCreateSchema,
  PromptVersionSchema,
  TenantTouchpointDetailSchema,
  TenantTouchpointSummarySchema,
  TouchpointDetailSchema,
  TouchpointSummarySchema,
  adminAuthContract,
  adminBrainContract,
  adminChatAskPath,
  adminChatContract,
  adminContract,
  adminCrewContract,
  adminCrewDocumentDownloadPath,
  adminDocumentAssetContentPath,
  adminDocumentDownloadPath,
  adminDocumentPreviewPath,
  adminDocumentsContract,
  adminJournalPhotoContentPath,
  adminJournalPhotoThumbnailPath,
  adminJournalsContract,
  adminProjectsContract,
  adminPromptsContract,
  adminRunsContract,
  adminTenantsContract
};
//# sourceMappingURL=index.js.map